import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ExternalLink } from 'lucide-react';
import { BRAND, COMPANIES } from '../data/content';
import { NAV } from '../constants/testIds';

const links = [
  { to: '/', label: 'Home', key: 'home' },
  { to: '/about', label: 'About', key: 'about' },
  { to: '/group-companies', label: 'Group Companies', key: 'groupCompanies', hasMega: true },
  { to: '/industries-served', label: 'Industries', key: 'industries' },
  { to: '/projects', label: 'Projects', key: 'projects' },
  { to: '/global-presence', label: 'Global Presence', key: 'globalPresence' },
  { to: '/careers', label: 'Careers', key: 'careers' },
  { to: '/contact', label: 'Contact', key: 'contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0D1B2A]/92 backdrop-blur-md border-b border-white/10'
          : 'bg-gradient-to-b from-[#0D1B2A]/70 to-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
          <div className="w-11 h-11 bg-white rounded-sm grid place-items-center overflow-hidden shrink-0">
            <img src={BRAND.logo} alt="Clan Group" className="w-9 h-9 object-contain" />
          </div>
          <div className="leading-tight">
            <div className="text-white font-display font-bold tracking-tight text-base">
              CLAN GROUP
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">
              Engineering Progress
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) =>
            l.hasMega ? (
              <div
                key={l.key}
                className="relative"
                onMouseEnter={() => setMegaOpen(true)}
                onMouseLeave={() => setMegaOpen(false)}
              >
                <NavLink
                  to={l.to}
                  data-testid={NAV[l.key]}
                  className={({ isActive }) =>
                    `px-3.5 py-2 text-[13px] font-medium tracking-wide flex items-center gap-1.5 transition-colors ${
                      isActive ? 'text-[#D4AF37]' : 'text-white/85 hover:text-white'
                    }`
                  }
                >
                  {l.label} <ChevronDown size={14} />
                </NavLink>
                {megaOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[680px]">
                    <div className="bg-white shadow-2xl border border-black/5 rounded-sm grid grid-cols-2 gap-1 p-2">
                      {COMPANIES.map((c) => (
                        <a
                          key={c.slug}
                          href={c.external ? c.href : c.href}
                          target={c.external ? '_blank' : undefined}
                          rel={c.external ? 'noreferrer' : undefined}
                          className="flex items-start gap-3 p-3 hover:bg-[#F5F7FA] transition-colors group"
                        >
                          <span
                            className="w-1 self-stretch rounded-sm shrink-0"
                            style={{ background: c.accent }}
                          />
                          <div className="min-w-0">
                            <div className="text-[13px] font-display font-bold text-[#0D1B2A] flex items-center gap-1.5">
                              {c.name}
                              {c.external && <ExternalLink size={11} className="text-[#D4AF37]" />}
                            </div>
                            <div className="text-[11px] text-[#4B5563] mt-0.5 truncate">
                              {c.discipline}
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={l.key}
                to={l.to}
                data-testid={NAV[l.key]}
                className={({ isActive }) =>
                  `px-3.5 py-2 text-[13px] font-medium tracking-wide transition-colors ${
                    isActive ? 'text-[#D4AF37]' : 'text-white/85 hover:text-white'
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <Link
            to="/contact"
            className="ml-3 bg-[#D4AF37] text-[#0D1B2A] text-[12px] font-bold uppercase tracking-[0.15em] px-4 py-2.5 hover:bg-white transition-colors"
          >
            Get in touch
          </Link>
        </nav>

        <button
          aria-label="Toggle menu"
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-white"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid={NAV.mobileMenu}
          className="lg:hidden bg-[#0D1B2A] border-t border-white/10 px-5 py-6"
        >
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                data-testid={`${NAV[l.key]}-mobile`}
                className={({ isActive }) =>
                  `py-3 text-base font-display font-medium border-b border-white/5 ${
                    isActive ? 'text-[#D4AF37]' : 'text-white'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-4 bg-[#D4AF37] text-[#0D1B2A] text-sm font-bold uppercase tracking-[0.15em] px-4 py-3 text-center"
            >
              Get in touch
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
